import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDownIcon,
  DocumentIcon,
  EnvelopeIcon,
  TickCircleIcon,
  WarningSignIcon,
} from "evergreen-ui";
import { statusClass } from "../invoiceUtils";
import { ModalLayer } from "./SharedUI";
import { getVisualViewportBounds, placeFloatingPanel } from "./floatingPosition";
import { usePrivacy } from "../privacy/PrivacyContext";
import { useUiLanguage } from "../uiLanguage";

const STATUS_OPTIONS = [
  { value: "Draft", detail: "Keep editable and unsent", icon: DocumentIcon, order: 0 },
  { value: "Sent", detail: "Awaiting client payment", icon: EnvelopeIcon, order: 1 },
  { value: "Partially paid", detail: "A payment has been allocated", icon: TickCircleIcon, order: 2 },
  { value: "Overdue", detail: "Payment is past its due date", icon: WarningSignIcon, order: 2 },
  { value: "Paid", detail: "Payment has been recorded", icon: TickCircleIcon, order: 3 },
];

const statusOrder = (status) => STATUS_OPTIONS.find((item) => item.value === status)?.order ?? 0;

const InvoiceStatusControl = ({ invoice, onChange, compact = false }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [reviewed, setReviewed] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const saving = useRef(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const connected = Boolean(invoice.accountStatus);
  const hasPayments = Number(invoice.paidAmount || 0) !== 0;
  const availableStatus = option => {
    if (option.value === invoice.status) return true;
    if (invoice.accountStatus === 'draft') return option.value === 'Sent';
    if (invoice.accountStatus !== 'sent') return false;
    return (option.value === 'Draft' && !hasPayments) || (option.value === 'Paid' && invoice.documentType !== 'credit-note');
  };
  const statusOptions = connected
    ? [...STATUS_OPTIONS.filter(availableStatus),
      ...(['draft', 'sent'].includes(invoice.accountStatus) && !hasPayments ? [{ value: 'Void', detail: 'Cancel this invoice; keep its history', icon: WarningSignIcon }] : [])]
    : invoice.documentType === "credit-note"
    ? STATUS_OPTIONS.filter((option) => ["Draft", "Sent"].includes(option.value))
    : STATUS_OPTIONS;

  useEffect(() => {
    if (!open) return undefined;
    const closeMenu = () => setOpen(false);
    const closeOnScroll = (event) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      closeMenu();
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeOnScroll, true);
    window.visualViewport?.addEventListener("resize", closeMenu);
    window.visualViewport?.addEventListener("scroll", closeMenu);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.visualViewport?.removeEventListener("resize", closeMenu);
      window.visualViewport?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (privacy.enabled || invoice.accountStatus === 'void') return <span className={`${statusClass(invoice.status)} is2-status-control__static`}>{t(invoice.status)}</span>;

  const choose = (status) => {
    setOpen(false);
    if (status === invoice.status) return;
    if (status === 'Void' || statusOrder(status) < statusOrder(invoice.status)) {
      setReviewed({ ...invoice }); setReason(''); setError('');
      setPending(status);
      return;
    }
    onChange(invoice.id, status, null, { ...invoice });
  };

  const confirm = async () => {
    if (saving.current || !reason.trim()) return;
    saving.current = true; setBusy(true); setError('');
    try {
      let rejection = '';
      if (await onChange(invoice.id, pending, reason.trim(), reviewed, message => { rejection = message; }) === false) setError(rejection || 'The change was not saved. Check the latest invoice or any pending change before trying again.');
      else setPending(null);
    } catch { setError('The change could not be confirmed. Check any pending change before trying again.'); }
    finally { saving.current = false; setBusy(false); }
  };

  const toggleMenu = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const nextPosition = placeFloatingPanel({ anchor: rect, width: 244, height: 274, align: "end", margin: 12, viewport: getVisualViewportBounds() });
      setPosition({ top: nextPosition.top, left: nextPosition.left, width: nextPosition.width, maxHeight: nextPosition.maxHeight });
    }
    setOpen(true);
  };

  return (
    <div className={`is2-status-control${compact ? " is-compact" : ""}`} onClick={(event) => event.stopPropagation()}>
      <button ref={triggerRef} className={`${statusClass(invoice.status)} is2-status-control__trigger`} aria-label={`Change status for ${privacy.invoice(invoice)}`} aria-expanded={open} onClick={toggleMenu}>{t(invoice.status)}<ChevronDownIcon size={12} /></button>
      {open ? <>
        <button className="is2-menu-dismiss" aria-label="Close status menu" onClick={() => setOpen(false)} />
        <div ref={menuRef} className="is2-inline-menu is2-status-menu" role="menu" aria-label={`Status for ${privacy.invoice(invoice)}`} style={position}>
          {statusOptions.map((option) => { const Icon = option.icon; const detail = invoice.documentType === "credit-note" && option.value === "Sent" ? "Issued to the client" : option.detail; return <button key={option.value} className={invoice.status === option.value ? "is-current" : ""} role="menuitemradio" aria-checked={invoice.status === option.value} onClick={() => choose(option.value)}><Icon size={15} /><span><strong>{t(option.value)}</strong><small>{t(detail)}</small></span>{invoice.status === option.value ? <TickCircleIcon size={14} /> : null}</button>; })}
          {connected && hasPayments ? <p style={{ padding: '8px 12px', margin: 0, fontSize: 13 }}>Reverse payments before returning to draft or voiding. Correct bank-linked payments in Banking.</p> : null}
        </div>
      </> : null}
      {pending ? <ModalLayer close={() => { if (!saving.current) setPending(null); }}>
        <section className="is2-confirmation is2-invoice-status-confirmation" role="alertdialog" aria-modal="true" aria-labelledby="status-confirm-title">
          <span className="is2-confirmation__icon"><WarningSignIcon size={19} /></span>
          <div><p className="is2-eyebrow">Confirm status change</p><h2 id="status-confirm-title">{pending === 'Void' ? `Void ${privacy.invoice(invoice)}?` : `Move ${privacy.invoice(invoice)} back to ${pending}?`}</h2><p>{pending === 'Void' ? 'This cancels the invoice. Its number and history are kept; it cannot be reopened.' : 'This makes the invoice editable again. It does not recall a delivered email.'}</p>
            {connected ? <p>Pending emails are cancelled and shared portal PDFs are withdrawn. Email already handed to the provider cannot be recalled.</p> : null}
            <label className="is2-invoice-status-reason"><span>Reason for correction</span><textarea aria-label="Reason for correction" required maxLength={1000} rows={3} value={reason} disabled={busy} onChange={event => setReason(event.target.value)} /></label>
            {error ? <p role="alert">{error}</p> : null}
          </div>
          <footer><button className="is2-button is2-button--quiet" disabled={busy} onClick={() => setPending(null)}>Keep {invoice.status}</button><button className="is2-button is2-button--primary" disabled={busy || !reason.trim()} onClick={confirm}>{busy ? 'Saving...' : `Change to ${pending}`}</button></footer>
        </section>
      </ModalLayer> : null}
    </div>
  );
};

export default InvoiceStatusControl;
